import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  lisResultsApi,
  type LisPdfMappingBody,
  type LisResultMapping,
  type LisResultMappingBody,
  type LisResultMappingRowBody,
  type LisResultRelease,
  type ReleaseResultsBody,
  type SendPdfBody,
} from '@/api/lisResultsApi'
import { useTenant } from '@/providers/TenantProvider'

/**
 * Outgoing result mappings and result releases (z8nrz7ffzv).
 *
 * `selectedTenantId` is in every key, so switching tenant cannot leave another
 * tenant's mappings or releases on screen.
 */

export function useLisResultMappings(integrationId: string | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-result-mappings', tenantId, integrationId],
    enabled: !!tenantId && !!integrationId,
    queryFn: () => lisResultsApi.listMappings(integrationId as string),
  })
}

export function useLisMappableTests() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-result-mappings', tenantId, 'tests'],
    enabled: !!tenantId,
    queryFn: () => lisResultsApi.mappableTests(),
  })
}

export function useLisProductResultFields() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-result-mappings', tenantId, 'product-fields'],
    enabled: !!tenantId,
    queryFn: () => lisResultsApi.productFields(),
    staleTime: 60 * 60 * 1000,
  })
}

/** Every mapping change answers with the mapping (or nothing); refetch the list. */
function useMappingMutation<TVars, TResult = LisResultMapping>(fn: (vars: TVars) => Promise<TResult>) {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation<TResult, Error, TVars>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lis-result-mappings', tenantId] })
    },
  })
}

export const useCreateLisResultMapping = () =>
  useMappingMutation(
    (body: LisResultMappingBody & { integrationId: string; testCode: string; formDefinitionId: string }) =>
      lisResultsApi.createMapping(body),
  )

export const useUpdateLisResultMapping = () =>
  useMappingMutation(({ id, body }: { id: string; body: LisResultMappingBody }) =>
    lisResultsApi.updateMapping(id, body),
  )

export const useRemoveLisResultMapping = () => useMappingMutation((id: string) => lisResultsApi.removeMapping(id))

export const useAddLisResultRow = () =>
  useMappingMutation(({ mappingId, body }: { mappingId: string; body: LisResultMappingRowBody }) =>
    lisResultsApi.addRow(mappingId, body),
  )

export const useUpdateLisResultRow = () =>
  useMappingMutation(({ rowId, body }: { rowId: string; body: Partial<LisResultMappingRowBody> }) =>
    lisResultsApi.updateRow(rowId, body),
  )

export const useRemoveLisResultRow = () => useMappingMutation((rowId: string) => lisResultsApi.removeRow(rowId))

export const useReorderLisResultRows = () =>
  useMappingMutation(({ mappingId, rowIds }: { mappingId: string; rowIds: string[] }) =>
    lisResultsApi.reorderRows(mappingId, rowIds),
  )

// ─── PDF mappings (z8nrz7fgcp) ───────────────────────────────────────────────
// Under the same key prefix, so any mapping change refetches both lists: a
// test sends results or a PDF, and each list marks the other's tests.

export function useLisPdfMappings(integrationId: string | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-result-mappings', tenantId, 'pdf', integrationId],
    enabled: !!tenantId && !!integrationId,
    queryFn: () => lisResultsApi.listPdfMappings(integrationId as string),
  })
}

export const useCreateLisPdfMapping = () =>
  useMappingMutation((body: LisPdfMappingBody & { integrationId: string }) =>
    lisResultsApi.createPdfMapping(body),
  )

export const useUpdateLisPdfMapping = () =>
  useMappingMutation(({ id, body }: { id: string; body: Partial<LisPdfMappingBody> }) =>
    lisResultsApi.updatePdfMapping(id, body),
  )

export const useRemoveLisPdfMapping = () => useMappingMutation((id: string) => lisResultsApi.removePdfMapping(id))

// ─── Releases ────────────────────────────────────────────────────────────────

/** The results this product can release, each with its releases (no order needed). */
export function useLisResultReports(productId: string) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-result-reports', tenantId, productId],
    enabled: !!tenantId && !!productId,
    queryFn: () => lisResultsApi.resultReports(productId),
  })
}

/** What a release would send; refetched each time the confirmation opens. */
export function useLisReleasePreview(body: ReleaseResultsBody) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-result-preview', tenantId, body],
    enabled: !!tenantId,
    queryFn: () => lisResultsApi.previewRelease(body),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
}

function useReleaseMutation<TVars>(fn: (vars: TVars) => Promise<LisResultRelease>) {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation<LisResultRelease, Error, TVars>({
    mutationFn: fn,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['lis-result-reports', tenantId] })
      // A retry can be a PDF's: its card reads the releases from here.
      void queryClient.invalidateQueries({ queryKey: ['lis-pdf-reports', tenantId] })
    },
  })
}

export const useReleaseLisResults = () =>
  useReleaseMutation((body: ReleaseResultsBody) => lisResultsApi.releaseResults(body))

/** Retry (undelivered) or Send again (not set up): the same release. */
export const useRetryLisResultRelease = () =>
  useReleaseMutation((releaseId: string) => lisResultsApi.retryRelease(releaseId))

// ─── Unsolicited PDF (z8nrz7fgcp) ────────────────────────────────────────────

/** The PDFs this product can send, each with its sends. */
export function useLisPdfReports(productId: string) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-pdf-reports', tenantId, productId],
    enabled: !!tenantId && !!productId,
    queryFn: () => lisResultsApi.pdfReports(productId),
  })
}

/** What Send PDF to Epic would send; refetched each time the confirmation opens. */
export function useLisPdfPreview(body: SendPdfBody) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-pdf-preview', tenantId, body],
    enabled: !!tenantId,
    queryFn: () => lisResultsApi.previewPdf(body),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
}

export const useSendLisPdf = () => useReleaseMutation((body: SendPdfBody) => lisResultsApi.sendPdf(body))
